"use client";

import React, { useContext, useEffect, useState } from "react";

import { LinkHandler } from "@/components/common/LinkHandler";
import { CartIcon } from "@/components/icons/CartIcon";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import CartContext from "@/context/CartContext";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { CartData } from "@/types/cart";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";
import { getMaskedId } from "@/utils/getMaskedId";

import styles from "./HeaderCartIcon.module.scss";

const envUrl = process.env.NEXT_PUBLIC_MAGENTO_URL ?? "";

interface HeaderCartIconProps {
  pathname?: string;
}

const HeaderCartIcon: React.FC<HeaderCartIconProps> = ({ pathname }) => {
  const [cartGender, setCartGender] = useState<string>("");
  const [cartLocalStorage, setCartLocalStorage] = useLocalStorage("cart", null);

  const cartState = useContext(CartContext);

  const setCartStateFromResponse = (cartJson: CartData | null) => {
    if (!cartJson) return;
    cartState.setCart(cartJson);
    setCartLocalStorage(cartJson);

    let gender = "";
    if (cartJson.items && Object.keys(cartJson.items).length > 0) {
      Object.values(cartJson.items).forEach((product) => {
        if (product?.product_gender === "Women") {
          gender = "women";
        }
      });
    }
    setCartGender(gender);
  };

  useEffect(() => {
    const cartItemsFromLocalStorage = cartLocalStorage?.items;
    let cartCount = 0;

    if (cartItemsFromLocalStorage) {
      cartCount = Object.keys(cartItemsFromLocalStorage).length;
    }

    /**
     * skip the header call and load from localstorage.
     * unless account page
     * case = user logged in to account with items already in cart
     */
    if (cartLocalStorage && cartCount > 0 && pathname !== "/account/") {
      setCartStateFromResponse(cartLocalStorage);
      return;
    }

    const getCart = () => {
      const maskedId = getMaskedId();
      const url = maskedId
        ? `${envUrl}rest/V1/nutrafol-carts/${maskedId}`
        : `${envUrl}rest/V1/nutrafol-carts/`;
      cartState.setIsCartFetching(true);
      fetch(url, { method: "GET" })
        .then((res) => res.json())
        .then((resJson) => {
          if (typeof resJson === "string") {
            const cartJson = JSON.parse(resJson);
            setCartStateFromResponse(cartJson);
          } else {
            setCartStateFromResponse(resJson);
          }
        })
        .catch((error) => {
          /* eslint-disable no-console */
          console.warn("Fetch error: ", error);
        })
        .finally(() => cartState.setIsCartFetching(false));
    };

    getCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Runs once on mount

  return (
    <>
      <div className="p-0 relative">
        <LinkHandler
          path={`/checkout/cart`}
          className="icon-holder icon-cart"
          id="cart-total-wrap"
          onClick={() =>
            GA4.onNavigationClicked({
              destination: "/checkout/cart/",
            })
          }
          ariaLabel={cartState.cartNum > 0 ? `Cart ${cartState.cartNum}` : "Cart"}
        >
          <div className="relative">
            <CartIcon className="w-6 h-6 text-nova-base-black" />
          </div>
          {cartState.cartNum > 0 && (
            <span className={cn(styles.cartQty)}>
              <span id="cart-total-items">
                <TextElementNova
                  element="span"
                  text={cartState.cartNum}
                  className="text-[9px] leading-[9px] block font-dinamoMono font-normal"
                  sanitize={true}
                />
              </span>
            </span>
          )}
        </LinkHandler>
        <input type="hidden" id="cart-gender" value={cartGender} />
      </div>
      {/*<MiniCart />*/}
    </>
  );
};

export default HeaderCartIcon;
